import lod_ from "lodash";
import { useEffect, useState, useCallback } from "react";
import {
	Dialog,
	DialogTitle,
	DialogContent,
	DialogActions,
	Button,
	Checkbox,
	IconButton,
	Icon,
	Tooltip
} from "@mui/material";
import MDBox from "components/Basics/MDBox";
import { t } from "i18next";
import MDTypography from "components/Basics/MDTypography";
import MDInput from "components/Basics/MDInput";
import MDButton from "components/Basics/MDButton";
import RowItem from "./components/RowItem";

/**
 * Display a table with all the files
 * User can select many files as he wants
 * @param {*} param0
 * @returns {JSX.Element} - StepSelect
 */
export default function StepSelect({ skeleton, setSkeleton, validStep, onChange }) {
	const [localFiles, setLocalFiles] = useState([]);
	const [batchDialogOpen, setBatchDialogOpen] = useState(false);
	const [batchHtmlSelector, setBatchHtmlSelector] = useState("");

	useEffect(() => {
		setLocalFiles(skeleton.files);
		validStep();
	}, []);

	// Fonction pour mettre à jour le htmlSelector de tous les fichiers en lot
	const handleBatchUpdateHtmlSelector = () => {
		const updatedFiles = localFiles.map(file => ({
			...file,
			metadatas: {
				...file.metadatas,
				htmlSelector: batchHtmlSelector
			}
		}));

		onChange(updatedFiles);
		setLocalFiles(updatedFiles);
		setBatchDialogOpen(false);
		setBatchHtmlSelector("");
	};

	// Memoized callbacks to prevent unnecessary re-renders
	const handleToggleSelection = useCallback(
		url => {
			setSkeleton(prev => {
				const isSelected = prev.selectedRows.includes(url);
				const newSelectedRows = isSelected
					? prev.selectedRows.filter(selectedUrl => selectedUrl !== url)
					: [...prev.selectedRows, url];

				return {
					...prev,
					selectedRows: newSelectedRows
				};
			});
		},
		[setSkeleton]
	);

	/**
	 * Toggle OCR for all files
	 */
	const toggleAllOCR = () => {
		// Determine if we need to enable or disable OCR based on current state
		const allHaveOCR = localFiles.every(file => file.metadatas?.extractTextFromImage);
		const newOCRState = !allHaveOCR;

		const updatedFiles = localFiles.map(file => ({
			...file,
			metadatas: {
				...file.metadatas,
				extractTextFromImage: newOCRState
			}
		}));
		onChange(updatedFiles);
		setLocalFiles(updatedFiles);
	};

	/**
	 * Change handler for each row item
	 * @param {string} url - URL of the file to change
	 * @param {string} key - Key to change
	 * @param {any} value - New value
	 */
	const onChangeRowItem = (url, key, value) => {
		const updatedFiles = localFiles.map(file => {
			if (file.url === url) {
				lod_.set(file, key, value);
			}
			return file;
		});

		onChange(updatedFiles);
	};

	return (
		<MDBox
			flex="1"
			style={{
				height: "100%",
				width: "100%",
				display: "flex",
				flexDirection: "column"
			}}
		>
			{/* Dialog pour la mise à jour en lot */}
			<Dialog
				open={batchDialogOpen}
				onClose={() => setBatchDialogOpen(false)}
				maxWidth="sm"
				fullWidth
			>
				<DialogTitle>
					<MDTypography variant="h6">{t("ASSETS.HTML_SELECTOR.editAllSelectors")}</MDTypography>
				</DialogTitle>
				<DialogContent>
					<MDBox pt={1}>
						<MDTypography variant="body2" mb={2}>
							{t("ASSETS.HTML_SELECTOR.editAllSelectorsDescription")}
						</MDTypography>
						<MDInput
							fullWidth
							value={batchHtmlSelector}
							onChange={e => setBatchHtmlSelector(e.target.value)}
							placeholder="Ex: article.content, .main-content, #content"
							label={t("ASSETS.HTML_SELECTOR.htmlSelector")}
						/>
					</MDBox>
				</DialogContent>
				<DialogActions>
					<Button onClick={() => setBatchDialogOpen(false)}>{t("SETTINGS.cancel")}</Button>
					<MDButton variant="gradient" color="info" onClick={handleBatchUpdateHtmlSelector}>
						{t("ASSETS.HTML_SELECTOR.applyToAll")}
					</MDButton>
				</DialogActions>
			</Dialog>

			{/* Table with headers and rows */}
			<MDBox
				sx={{
					border: "1px solid #e0e0e0",
					overflow: "auto",
					borderRadius: 1
				}}
			>
				<table style={{ width: "100%", borderCollapse: "collapse" }}>
					<MDBox component="tr" style={{ backgroundColor: "#f5f5f5" }}>
						<MDBox
							component="th"
							style={{
								width: "5%",
								padding: "12px 16px",
								textAlign: "left",
								borderBottom: "2px solid #e0e0e0"
							}}
						>
							<Checkbox
								checked={
									skeleton.selectedRows.length > 0 &&
									skeleton.selectedRows.length === localFiles.length
								}
								onChange={e => {
									if (e.target.checked) {
										// Select all
										const allUrls = localFiles.map(file => file.url);
										setSkeleton(prev => ({ ...prev, selectedRows: allUrls }));
									} else {
										// Deselect all
										setSkeleton(prev => ({ ...prev, selectedRows: [] }));
									}
								}}
							/>
						</MDBox>
						<MDBox
							component="th"
							style={{
								width: "25%",
								padding: "12px 16px",
								textAlign: "left",
								borderBottom: "2px solid #e0e0e0"
							}}
						>
							<MDTypography variant="body2" fontWeight="bold" fontSize="small">
								{t("SOURCES.DISPLAY.name")}
							</MDTypography>
						</MDBox>
						<MDBox
							component="th"
							style={{
								width: "12%",
								padding: "12px 16px",
								textAlign: "left",
								borderBottom: "2px solid #e0e0e0"
							}}
						>
							<MDTypography variant="body2" fontWeight="bold" fontSize="small">
								{t("ASSETS.DETAILS.language")}
							</MDTypography>
						</MDBox>
						<MDBox
							component="th"
							style={{
								width: "18%",
								padding: "12px 16px",
								textAlign: "left",
								borderBottom: "2px solid #e0e0e0"
							}}
						>
							<MDBox display="flex" alignItems="center">
								<MDTypography variant="body2" fontWeight="bold" fontSize="small">
									{t("ASSETS.HTML_SELECTOR.htmlSelector")}
								</MDTypography>
								<Tooltip title={t("ASSETS.HTML_SELECTOR.changeForAll")} placement="top">
									<IconButton
										size="small"
										sx={{ ml: 2 }}
										color="info"
										onClick={() => setBatchDialogOpen(true)}
									>
										<Icon>edit</Icon>
									</IconButton>
								</Tooltip>
							</MDBox>
						</MDBox>
						<MDBox
							component="th"
							style={{
								width: "15%",
								padding: "12px 16px",
								textAlign: "center",
								borderBottom: "2px solid #e0e0e0"
							}}
						>
							<MDBox display="flex" alignItems="center" justifyContent="center">
								<MDTypography variant="body2" fontWeight="bold" fontSize="small">
									{t("SOURCES.EXTRACT.extractTextImageTitle")}
								</MDTypography>
								<Tooltip title={t("ASSETS.HTML_SELECTOR.changeForAll")} placement="top">
									<IconButton size="small" color="info" onClick={toggleAllOCR}>
										<Icon>edit</Icon>
									</IconButton>
								</Tooltip>
							</MDBox>
						</MDBox>
						<MDBox
							component="th"
							style={{
								width: "25%",
								padding: "12px 16px",
								textAlign: "left",
								borderBottom: "2px solid #e0e0e0"
							}}
						>
							<MDTypography variant="body2" fontWeight="bold" fontSize="small">
								{t("SOURCES.DISPLAY.url")}
							</MDTypography>
						</MDBox>
					</MDBox>

					<MDBox component="tbody">
						{localFiles.map((file, index) => (
							<RowItem
								key={index}
								file={file}
								isSelected={skeleton.selectedRows.includes(file.url)}
								onToggleSelection={handleToggleSelection}
								onChange={onChangeRowItem}
							/>
						))}
					</MDBox>
				</table>
			</MDBox>
		</MDBox>
	);
}
